import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { API, ax, errorText, WAREHOUSE_CAPABILITIES } from "./common";

const EMPTY = { code: "", name: "", country: "", capabilities: [], address: "", contact_phone: "", contact_email: "", contact_person_name: "" };
const OPTIONAL = ["address", "contact_phone", "contact_email", "contact_person_name"];

export default function WarehousesTab({ showMsg, refreshTick = 0 }) {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async (silent = false) => {
    try {
      const { data } = await axios.get(`${API}/warehouses`, ax);
      setRows(data);
    } catch (e) { if (!silent) showMsg("error", errorText(e, t("adm.error"))); }
  }, [showMsg, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshTick) load(true); }, [refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => { setEditingId(null); setForm({ ...EMPTY }); };
  const openEdit = (w) => {
    setEditingId(w.id);
    setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, w[k] ?? EMPTY[k]])) });
  };

  const toggleCapability = (cap, checked) => setForm(f => ({
    ...f,
    capabilities: checked ? [...f.capabilities, cap] : f.capabilities.filter(c => c !== cap),
  }));

  const save = async () => {
    const payload = { ...form };
    OPTIONAL.forEach(k => { payload[k] = payload[k]?.trim() || null; });
    try {
      if (editingId) {
        delete payload.code;
        await axios.put(`${API}/warehouses/${editingId}`, payload, ax);
        showMsg("success", t("adm.wh.updated"));
      } else {
        await axios.post(`${API}/warehouses`, payload, ax);
        showMsg("success", t("adm.wh.created"));
      }
      setForm(null);
      load();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  const setActive = async (w, active) => {
    try {
      await axios.post(`${API}/warehouses/${w.id}/${active ? "activate" : "deactivate"}`, {}, ax);
      showMsg("success", t(active ? "adm.wh.activated" : "adm.wh.deactivated"));
      load();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  const valid = form && form.name.trim() && form.country.trim() && form.capabilities.length > 0 && (editingId || form.code.trim());

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={openCreate} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4" /> {t("adm.wh.new")}
        </Button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-50">
        {rows.length === 0 && <p className="p-8 text-center text-slate-400 text-sm">{t("adm.wh.empty")}</p>}
        {rows.map(w => (
          <div key={w.id} className="flex items-center gap-3 px-4 py-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 text-sm truncate">{w.name} <span className="font-mono text-xs text-slate-400">{w.code}</span></p>
              <p className="text-xs text-slate-500">{w.country} · {(w.capabilities || []).map(c => t(`adm.cap.${c}`)).join(", ")}</p>
            </div>
            <span className={`text-xs font-medium px-2 py-1 rounded-lg ${w.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
              {t(w.active ? "adm.active" : "adm.inactive")}
            </span>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" onClick={() => openEdit(w)} aria-label={t("common.edit")}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setActive(w, !w.active)}>
              {t(w.active ? "adm.deactivate" : "adm.activate")}
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!form} onOpenChange={(open) => !open && setForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t(editingId ? "adm.wh.edit" : "adm.wh.new")}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>{t("adm.code")} *</Label>
                <Input value={form.code} disabled={!!editingId} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} className="h-10 font-mono uppercase" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("common.name")} *</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-10" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("adm.country")} *</Label>
                  <Input value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} className="h-10" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t("adm.wh.capabilities")} *</Label>
                <div className="flex gap-4">
                  {WAREHOUSE_CAPABILITIES.map(cap => (
                    <label key={cap} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.capabilities.includes(cap)} onCheckedChange={v => toggleCapability(cap, v === true)} />
                      {t(`adm.cap.${cap}`)}
                    </label>
                  ))}
                </div>
              </div>
              {[["address", "adm.wh.address"], ["contact_person_name", "adm.wh.contact_person"],
                ["contact_phone", "adm.wh.phone"], ["contact_email", "adm.wh.email"]].map(([field, key]) => (
                <div key={field} className="space-y-1.5">
                  <Label>{t(key)}</Label>
                  <Input value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} className="h-10" />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={save} disabled={!valid} className="bg-blue-600 hover:bg-blue-700">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
